import {
  TPlansRankingResponse,
  TProductsRankingResponse,
} from "../dashboard.types";

interface RankingCardProps {
  productsRankingData: TProductsRankingResponse | null;
  plansRankingData: TPlansRankingResponse | null;
  isLoading: boolean;
  isPlansView: boolean;
}

function RankingHeader({ isPlansView }: { isPlansView: boolean }) {
  return (
    <div className="flex-shrink-0 w-full">
      <div className="flex items-left justify-left">
        <div className="font-['Poppins',_sans-serif] text-foreground dark:text-white text-[18px] text-center">
          <p className="leading-tight text-left">
            {isPlansView ? "Ranking de Planos" : "Ranking de Produtos"}
          </p>
        </div>
      </div>
    </div>
  );
}

function RankingItem({
  name,
  description,
  salesCount,
  position,
  isTop,
}: {
  name: string;
  description?: string | null;
  salesCount: number;
  position: number;
  isTop: boolean;
}) {
  const formatQuantity = (value: number) => {
    return new Intl.NumberFormat("pt-BR").format(value);
  };

  return (
    <div className="rounded-[15px] w-full border border-border dark:border-[#1A1A1A] flex-shrink-0 relative">
      <div className="absolute border border-[rgba(255,255,255,0.01)] border-solid inset-0 pointer-events-none rounded-[15px]" />
      <div className="flex items-center px-3 py-[15px] relative">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
            isTop ? "bg-[#22c55e] text-white" : "bg-[#ef4444] text-white"
          }`}>
            {position}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-['Poppins',_sans-serif] font-medium text-foreground dark:text-white text-[12px] text-left truncate">
              <p className="leading-tight">{name}</p>
            </div>
            {description && (
              <div className="font-['Poppins',_sans-serif] text-muted-foreground dark:text-gray-400 text-[10px] text-left truncate mt-0.5">
                <p className="leading-tight">{description}</p>
              </div>
            )}
          </div>
        </div>
        <div className="flex-shrink-0 ml-2">
          <div className={`font-['Poppins',_sans-serif] font-semibold text-[14px] ${
            isTop ? "text-[#22c55e]" : "text-[#ef4444]"
          }`}>
            <p className="leading-none">{formatQuantity(salesCount)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function RankingsList({
  productsRankingData,
  plansRankingData,
  isLoading,
  isPlansView,
}: RankingCardProps) {
  if (isLoading) {
    return (
      <div className="flex-1 w-full min-h-0 flex flex-col items-center justify-center">
        <div className="text-muted-foreground dark:text-gray-400 text-sm">
          Carregando...
        </div>
      </div>
    );
  }

  if (isPlansView) {
    if (!plansRankingData) {
      return (
        <div className="flex-1 w-full min-h-0 flex flex-col items-center justify-center">
          <div className="text-muted-foreground dark:text-gray-400 text-sm">
            Nenhum dado disponível
          </div>
        </div>
      );
    }

    return (
      <div className="flex-1 w-full min-h-0 flex flex-col">
        <div className="flex flex-col gap-3 flex-1">
          {/* Mais Vendidos */}
          <div className="flex flex-col gap-2">
            <div className="font-['Poppins',_sans-serif] text-foreground dark:text-white text-[14px] font-semibold">
              <p className="leading-tight text-left">Mais Vendidos</p>
            </div>
            <div className="flex flex-col gap-2">
              {plansRankingData.most_sold.slice(0, 3).map((plan, index) => (
                <RankingItem
                  key={plan.plan_id}
                  name={plan.plan_name}
                  description={plan.plan_description}
                  salesCount={plan.sales_count}
                  position={index + 1}
                  isTop={true}
                />
              ))}
            </div>
          </div>

          {/* Menos Vendidos */}
          <div className="flex flex-col gap-2">
            <div className="font-['Poppins',_sans-serif] text-foreground dark:text-white text-[14px] font-semibold">
              <p className="leading-tight text-left">Menos Vendidos</p>
            </div>
            <div className="flex flex-col gap-2">
              {plansRankingData.least_sold.slice(0, 3).map((plan, index) => (
                <RankingItem
                  key={plan.plan_id}
                  name={plan.plan_name}
                  description={plan.plan_description}
                  salesCount={plan.sales_count}
                  position={index + 1}
                  isTop={false}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    if (!productsRankingData) {
      return (
        <div className="flex-1 w-full min-h-0 flex flex-col items-center justify-center">
          <div className="text-muted-foreground dark:text-gray-400 text-sm">
            Nenhum dado disponível
          </div>
        </div>
      );
    }

    return (
      <div className="flex-1 w-full min-h-0 flex flex-col">
        <div className="flex flex-col gap-3 flex-1">
          {/* Mais Vendidos */}
          <div className="flex flex-col gap-2">
            <div className="font-['Poppins',_sans-serif] text-foreground dark:text-white text-[14px] font-semibold">
              <p className="leading-tight text-left">Mais Vendidos</p>
            </div>
            <div className="flex flex-col gap-2">
              {productsRankingData.most_sold.slice(0, 3).map((product, index) => (
                <RankingItem
                  key={product.product_id}
                  name={product.product_name}
                  salesCount={product.sales_count}
                  position={index + 1}
                  isTop={true}
                />
              ))}
            </div>
          </div>

          {/* Menos Vendidos */}
          <div className="flex flex-col gap-2">
            <div className="font-['Poppins',_sans-serif] text-foreground dark:text-white text-[14px] font-semibold">
              <p className="leading-tight text-left">Menos Vendidos</p>
            </div>
            <div className="flex flex-col gap-2">
              {productsRankingData.least_sold.slice(0, 3).map((product, index) => (
                <RankingItem
                  key={product.product_id}
                  name={product.product_name}
                  salesCount={product.sales_count}
                  position={index + 1}
                  isTop={false}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export function RankingCard({
  productsRankingData,
  plansRankingData,
  isLoading,
  isPlansView,
}: RankingCardProps) {
  return (
    <div className="relative rounded-[30px] h-full w-full border border-border dark:border-[#1A1A1A] flex flex-col">
      <div className="absolute border border-[rgba(255,255,255,0.01)] border-solid inset-0 pointer-events-none rounded-[30px]" />
      <div className="flex flex-col p-4 h-full w-full gap-3 min-h-0">
        <RankingHeader isPlansView={isPlansView} />
        <RankingsList
          productsRankingData={productsRankingData}
          plansRankingData={plansRankingData}
          isLoading={isLoading}
          isPlansView={isPlansView}
        />
      </div>
    </div>
  );
}

